<template>
  <div
    class="title-group"
    :style="rootStyle"
    :class="rootClass"
  >
    <div class="title-group__content">
      <h1 class="title-group__title">
        <span
          v-if="rubric.icon"
          class="title-group__title-icon"
        >
          <img
            :src="rubric.icon"
            :alt="`Иконка ${typePage} ${rubric.title}`"
            height="32"
            width="32"
          />
        </span>
        <span class="title-group__title-text">{{ rubric.title }}</span>
      </h1>
      <div
        v-if="rubric.parsed_description.length > 0 && isFirstPage"
        class="title-group__description"
      >
        <TheContent
          v-for="item in rubric.parsed_description"
          :key="item.id"
          :elem="item"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { IRubric } from '~/types/api/archives';
  import type { TImageSize } from '~/types/common';
  import { BREAKPOINTS } from '~/constants';
  import TheContent from '~/components/TheContent';
  import { useWindowStore } from '~/store/window';

  export default defineNuxtComponent({
    name: 'BaseTitleGroup',
    components: {
      TheContent,
    },

    props: {
      rubric: {
        type: Object as PropType<IRubric>,
        required: true,
      },

      isFirstPage: {
        type: Boolean as PropType<boolean>,
        required: true,
      },

      isSimple: {
        type: Boolean as PropType<boolean>,
        required: true,
      },
    },

    data: () => ({
      BREAKPOINTS,
    }),

    computed: {
      windowWidth(): number {
        return useWindowStore().windowWidth;
      },

      isMobile(): boolean {
        return useWindowStore().isMobile;
      },

      typePage(): string {
        const routeName = useRoute().name;
        switch (routeName) {
          case 'archive-tag-name':
            return 'тэга';
          case 'archive-category-slug':
            return 'категории';
          default:
            return '';
        }
      },

      isCoverImage(): boolean {
        return this.rubric.covers.length > 0 && this.isFirstPage;
      },

      coverImages(): TImageSize[] {
        const sizes = [368, 736, 748, 1128];
        return this.rubric.covers.filter((item) => sizes.includes(item.width));
      },

      rootStyle() {
        const color =
          (this.isMobile && this.isFirstPage && this.rubric.color) ||
          (!this.isMobile && this.isFirstPage && !this.rubric.covers.length && this.rubric.color);

        return {
          '--gradient-cover-1': `${color || '#FFFFFF'}`,
          '--wrapp-padding-top': this.rubric.color ? '24px' : '0px',
          '--title-padding-bottom': this.isFirstPage ? '0px' : '16px',
        };
      },

      rootClass() {
        return {
          'title-group--simple': this.isSimple,
          'title-group--with-cover': this.isCoverImage,
        };
      },

      coverStyle() {
        const color = !this.isMobile && this.rubric.color;

        return {
          '--gradient-cover-1': `${color || '#FFFFFF'}`,
        };
      },
    },
  });
</script>

<style>
  :root {
    --gradient-cover-1: #ffffff;
    --gradient-cover-2: #ffffff;
  }
</style>

<style lang="scss" scoped>
  .title-group {
    $title-group: &;

    font-family: $text-font--1;
    border-radius: 32px;
    padding: 24px 16px 24px;
    margin: 14px 14px 16px;
    background: linear-gradient(180deg, var(--gradient-cover-1) 0%, var(--gradient-cover-2) 72.26%);

    @include tablet {
      border-radius: 48px 48px 0 0;
      padding: var(--wrapp-padding-top) 24px 8px;
      margin: 0;
    }

    @include big-tablet {
      padding: var(--wrapp-padding-top) 32px 8px;
    }

    @include desktop {
      padding: var(--wrapp-padding-top) 0 0;
    }

    &--with-cover {
      @include tablet {
        padding: 0px 0px 8px;
      }
      @include big-tablet {
        padding: 0px 0px 12px;
      }
      @include desktop {
        padding: 0;
      }

      .title-group__content {
        padding: 0;
        @include tablet {
          padding: 0 24px;
        }
        @include big-tablet {
          padding: 0 40px;
        }
        @include desktop {
          padding: 0 32px;
        }
      }
    }

    &--simple {
      margin: 0 0 -1px 0;
      padding: 24px 16px var(--title-padding-bottom);
      border-radius: 32px 32px 0 0;
      @include tablet {
        padding: 0px 24px 8px;
      }
      @include big-tablet {
        padding: 0px 32px 0;
      }
      @include desktop {
        padding: 0;
      }
      .title-group__content {
        margin-bottom: 0;
      }
    }

    &__cover-wrapp {
      @include tablet {
        padding: 24px 24px 8px;
        border-radius: 48px 48px 0 0;
        background: linear-gradient(
          180deg,
          var(--gradient-cover-1) 0%,
          var(--gradient-cover-2) 72.26%
        );
      }

      @include big-tablet {
        padding: 32px 32px 16px;
      }

      @include desktop {
        padding: 40px 40px 20px;
      }
    }

    &__cover-image {
      display: block;
      width: 100%;
      height: 120px;
      margin-bottom: 16px;

      @include tablet {
        height: 224px;
      }

      @include big-tablet {
        height: 280px;
        padding: 0 10px;
      }

      @include desktop {
        padding: 0;
        height: 360px;
      }

      img {
        width: 100%;
        height: 100%;
        border-radius: 200px;
        object-fit: cover;
      }
    }

    &__content {
      // padding: 24px 16px 0;
      @include tablet {
        // padding: 0 24px;
      }

      @include big-tablet {
        // padding: 0 32px;
        padding: 0 8px;
      }

      @include desktop {
        padding: 0 40px;
        margin-bottom: 8px;
      }
    }

    &__title {
      font-family: $headline-font--1;
      font-weight: 400;
      display: flex;
      align-items: center;
    }

    &__title-icon {
      margin-right: 12px;
      display: block;
      width: 32px;
      height: 32px;

      img {
        display: block;
        width: 100%;
        height: 100%;
      }
    }

    &__title-text {
      font-size: 24px;
      line-height: 30px;

      @include tablet {
        font-size: 32px;
        line-height: 42px;
      }
    }

    &__description {
      word-break: break-word;
      @include normalText;
      margin-top: $spacing-2;
      @include tablet {
        margin-top: $spacing-4;
      }

      @include desktop {
        @include mediumText;
      }

      :deep() {
        p {
          white-space: pre-line;
        }
        a {
          @include link-styles;
        }
        ul,
        ol {
          margin-bottom: $spacing-3;
          margin-top: $spacing-3;

          @include tablet {
            margin-top: $spacing-4;
            margin-bottom: $spacing-4;
          }
        }
      }
    }
  }
</style>
